import React, { useEffect, useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import cardBack from "../../@shared/assets/back-of-card.png";
import AudioManager from "../../@shared/utils/AudioManager";

const CARD_SIZE_DEFAULT = 140;

/**
 * TradeCardAnimation
 * - version=1: classic cross and flash
 * - version=2: 3D deck lift and shuffle
 * - version=3: poker table side pass
 * - version=4: stacked cut-and-pass
 * - version=5: orbital spin trade
 * - version=6: boomerang fakeout
 * - version=7: vertical helix exchange
 * - version=8: mirror snap teleport
 */
export default function TradeCardAnimation({
  version = 1,
  timestamp,
  onFinish,
  size = CARD_SIZE_DEFAULT,
  imgSrc = cardBack,
  zIndex = 3000,
  flashColor = "#fff",
  disableSfx = false,
  sfx = {
    enter: "cardSwooshIn",
    beat: "cardShuffle",
    swap: "cardSwoosh",
    exit: null,
  },
}) {
  const cardA = useAnimation();
  const cardB = useAnimation();
  const flash = useAnimation();

  const mounted = useRef(true);
  const [zA, setZA] = useState(2);
  const [zB, setZB] = useState(1);
  const playTradeSfx = (name) => {
    if (!disableSfx && name) {
      AudioManager.playSFX?.(name);
    }
  };

  useEffect(() => {
    mounted.current = true;

    const VARIANTS = {
      1: {
        run: async () => {
          const durationIn = 0.6;
          await Promise.all([
            cardA.start({
              x: 0,
              y: 0,
              transition: { duration: durationIn, ease: "easeOut" },
            }),
            cardB.start({
              x: 0,
              y: 0,
              transition: { duration: durationIn, ease: "easeOut" },
            }),
          ]);

          if (!mounted.current) return;
          await wait(0.4);
          if (!mounted.current) return;

          playTradeSfx(sfx.enter || "cardSwooshIn");
          flash.start({
            opacity: [0, 0.8, 0],
            transition: { duration: 0.23, ease: "linear", times: [0, 0.8, 1] },
          });

          await Promise.all([
            cardA.start({
              x: 200,
              y: -400,
              transition: { duration: durationIn, ease: "easeIn" },
            }),
            cardB.start({
              x: -200,
              y: 400,
              transition: { duration: durationIn, ease: "easeIn" },
            }),
            cardA.start({ opacity: 0, transition: { duration: 0.5, ease: "easeInOut" } }),
            cardB.start({ opacity: 0, transition: { duration: 0.5, ease: "easeInOut" } }),
          ]);
        },
      },

      2: {
        run: async () => {
          await Promise.all([
            cardA.start({
              scale: [1, 1.5, 2.5, 0.8],
              x: [0, 0, 0, 0],
              y: [300, 360, 300, 20],
              rotateY: [0, 0, 0, 0],
              rotateX: [13, 78, 78, 45],
              transformPerspective: [800, 300, 300, 500],
              opacity: [1, 1, 1, 1],
              transition: {
                duration: 0.95,
                ease: "easeInOut",
                times: [0, 0.45, 0.75, 1],
              },
            }),
            cardB.start({
              scale: [1, 1, 1, 0.8],
              x: [0, 0, 0, 0],
              y: [-300, -300, -300, 0],
              rotateY: [0, 0, 0, 0],
              rotateX: [45, 45, 45, 45],
              transformPerspective: [300, 500, 500, 500],
              opacity: [1, 1, 1, 1],
              transition: {
                duration: 0.95,
                ease: "easeInOut",
                times: [0, 0.45, 0.75, 1],
              },
            }),
          ]);

          if (!mounted.current) return;
          playTradeSfx(sfx.beat || "cardShuffle");

          const OFFSET = size * 0.42;
          const LIFT = 26;
          const TILT = 6;
          const BEATS = 2;

          for (let b = 0; b < BEATS; b++) {
            await Promise.all([
              cardA.start({
                x: [-OFFSET],
                y: [0, -LIFT, 0],
                rotateZ: [0, -TILT, 0],
                rotateX: [38, 35, 38],
                transformPerspective: [300],
                transition: { duration: 0.18, ease: "easeInOut", times: [0, 0.5, 1] },
              }),
              cardB.start({
                x: [OFFSET],
                y: [0, -LIFT, 0],
                rotateZ: [0, TILT, 0],
                rotateX: [38, 35, 38],
                transformPerspective: [300],
                transition: { duration: 0.18, ease: "easeInOut", times: [0, 0.5, 1] },
              }),
            ]);

            if (b % 2 === 0) {
              setZA(1);
              setZB(2);
            } else {
              setZA(2);
              setZB(1);
            }

            await Promise.all([
              cardA.start({
                x: [-OFFSET, -OFFSET * 0.25, 0],
                y: [0, -4, 0],
                rotateZ: [0, -2, 0],
                rotateX: [38, 36, 38],
                transformPerspective: [600, 650, 600],
                transition: { duration: 0.2, ease: "easeInOut", times: [0, 0.6, 1] },
              }),
              cardB.start({
                x: [OFFSET, OFFSET * 0.25, 0],
                y: [0, -4, 0],
                rotateZ: [0, 2, 0],
                rotateX: [38, 36, 38],
                transformPerspective: [600, 650, 600],
                transition: { duration: 0.2, ease: "easeInOut", times: [0, 0.6, 1] },
              }),
            ]);
          }

          if (!mounted.current) return;

          flash.start({
            opacity: [0, 0.85, 0],
            transition: { duration: 0.18, ease: "linear", times: [0, 0.5, 1] },
          });
          setZA(1);
          setZB(2);
          playTradeSfx(sfx.swap);

          const EASE_OUT = [0.22, 0.61, 0.36, 1];
          await Promise.all([
            cardA.start({
              x: [0, 0, 0],
              y: [0, -80, -440],
              rotateZ: [0, 0, 0],
              rotateX: [38, 12, -70],
              transformPerspective: [600, 650, 600],
              scale: [1, 0.98, 0.9],
              filter: ["blur(0px)", "blur(0px)", "blur(1px)"],
              opacity: [1, 1, 0],
              transition: { duration: 0.54, ease: EASE_OUT, times: [0, 0.45, 1] },
            }),
            cardB.start({
              x: [0, 0, 0],
              y: [0, 90, 460],
              rotateZ: [0, 0, 0],
              rotateX: [38, 58, 72],
              transformPerspective: [600, 700, 850],
              scale: [1, 1.02, 1.08],
              filter: ["", "blur(0px)", "blur(1px)"],
              opacity: [1, 1, 0],
              transition: {
                duration: 0.54,
                ease: EASE_OUT,
                times: [0, 0.45, 1],
                delay: 0.06,
              },
            }),
          ]);

          playTradeSfx(sfx.exit);
          await Promise.all([
            cardA.start({ opacity: 0, transition: { duration: 0.12 } }),
            cardB.start({ opacity: 0, transition: { duration: 0.12 } }),
          ]);
        },
      },

      3: {
        run: async () => {
          await Promise.all([
            cardA.start({
              x: [-360, -170, 0],
              y: [120, 42, 0],
              rotateZ: [-10, -4, 0],
              scale: [0.94, 1.02, 1.04],
              transition: { duration: 0.45, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.7, 1] },
            }),
            cardB.start({
              x: [360, 170, 0],
              y: [-120, -42, 0],
              rotateZ: [10, 4, 0],
              scale: [0.94, 1.02, 1.04],
              transition: { duration: 0.45, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.7, 1] },
            }),
          ]);

          if (!mounted.current) return;
          playTradeSfx(sfx.beat || "cardShuffle");

          setZA(2);
          setZB(1);
          await Promise.all([
            cardA.start({
              x: [0, 34, 70],
              y: [0, -8, -20],
              rotateZ: [0, 8, 12],
              transition: { duration: 0.22, ease: "easeInOut", times: [0, 0.5, 1] },
            }),
            cardB.start({
              x: [0, -34, -70],
              y: [0, 8, 20],
              rotateZ: [0, -8, -12],
              transition: { duration: 0.22, ease: "easeInOut", times: [0, 0.5, 1] },
            }),
          ]);

          setZA(1);
          setZB(2);
          flash.start({
            opacity: [0, 0.75, 0],
            transition: { duration: 0.16, ease: "linear" },
          });
          playTradeSfx(sfx.swap);

          await Promise.all([
            cardA.start({
              x: [70, 220, 380],
              y: [-20, -65, -150],
              rotateZ: [12, 4, -4],
              opacity: [1, 1, 0],
              transition: { duration: 0.4, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.55, 1] },
            }),
            cardB.start({
              x: [-70, -220, -380],
              y: [20, 65, 150],
              rotateZ: [-12, -4, 4],
              opacity: [1, 1, 0],
              transition: { duration: 0.4, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.55, 1] },
            }),
          ]);
        },
      },

      4: {
        run: async () => {
          await Promise.all([
            cardA.start({
              y: [260, 80, 0],
              scale: [0.92, 1, 1.03],
              rotateZ: [-4, -2, 0],
              transition: { duration: 0.42, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.76, 1] },
            }),
            cardB.start({
              y: [-260, -80, 0],
              scale: [0.92, 1, 1.03],
              rotateZ: [4, 2, 0],
              transition: { duration: 0.42, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.76, 1] },
            }),
          ]);

          if (!mounted.current) return;
          playTradeSfx(sfx.beat || "cardShuffle");

          setZA(2);
          setZB(1);
          await cardA.start({
            y: [0, -20, -6],
            rotateZ: [0, -6, -2],
            transition: { duration: 0.2, ease: "easeInOut", times: [0, 0.5, 1] },
          });

          setZA(1);
          setZB(2);
          await cardB.start({
            y: [0, 20, 6],
            rotateZ: [0, 6, 2],
            transition: { duration: 0.2, ease: "easeInOut", times: [0, 0.5, 1] },
          });

          flash.start({
            opacity: [0, 0.82, 0],
            transition: { duration: 0.14, ease: "linear" },
          });
          playTradeSfx(sfx.swap);

          await Promise.all([
            cardA.start({
              x: [0, -20, -70],
              y: [-6, -28, -86],
              rotateZ: [-2, -10, -14],
              scale: [1.03, 1, 0.95],
              opacity: [1, 1, 0],
              transition: { duration: 0.36, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.58, 1] },
            }),
            cardB.start({
              x: [0, 20, 70],
              y: [6, 28, 86],
              rotateZ: [2, 10, 14],
              scale: [1.03, 1.04, 1.08],
              opacity: [1, 1, 0],
              transition: { duration: 0.36, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.58, 1] },
            }),
          ]);
        },
      },

      5: {
        run: async () => {
          await Promise.all([
            cardA.start({
              x: [-260, -170, -80],
              y: [0, -90, -40],
              rotateZ: [-12, -26, -12],
              scale: [0.95, 1.02, 1.05],
              transition: { duration: 0.35, ease: "easeOut", times: [0, 0.6, 1] },
            }),
            cardB.start({
              x: [260, 170, 80],
              y: [0, 90, 40],
              rotateZ: [12, 26, 12],
              scale: [0.95, 1.02, 1.05],
              transition: { duration: 0.35, ease: "easeOut", times: [0, 0.6, 1] },
            }),
          ]);

          if (!mounted.current) return;
          playTradeSfx(sfx.beat || "cardShuffle");

          setZA(2);
          setZB(1);
          await Promise.all([
            cardA.start({
              x: [-80, 0, 65],
              y: [-40, -70, -10],
              rotateZ: [-12, -2, 8],
              transition: { duration: 0.24, ease: "easeInOut", times: [0, 0.6, 1] },
            }),
            cardB.start({
              x: [80, 0, -65],
              y: [40, 70, 10],
              rotateZ: [12, 2, -8],
              transition: { duration: 0.24, ease: "easeInOut", times: [0, 0.6, 1] },
            }),
          ]);

          setZA(1);
          setZB(2);
          flash.start({
            opacity: [0, 0.88, 0],
            transition: { duration: 0.16, ease: "linear" },
          });
          playTradeSfx(sfx.swap);

          await Promise.all([
            cardA.start({
              x: [65, 130, 240],
              y: [-10, 56, 180],
              rotateZ: [8, -4, -16],
              opacity: [1, 1, 0],
              transition: { duration: 0.42, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.55, 1] },
            }),
            cardB.start({
              x: [-65, -130, -240],
              y: [10, -56, -180],
              rotateZ: [-8, 4, 16],
              opacity: [1, 1, 0],
              transition: { duration: 0.42, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.55, 1] },
            }),
          ]);
        },
      },

      6: {
        run: async () => {
          // hard approach toward center
          await Promise.all([
            cardA.start({
              x: [-200, -70, 0],
              y: [220, 80, 0],
              rotateZ: [-18, -8, 0],
              scale: [0.9, 1.02, 1.05],
              transition: { duration: 0.34, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.72, 1] },
            }),
            cardB.start({
              x: [200, 70, 0],
              y: [-220, -80, 0],
              rotateZ: [18, 8, 0],
              scale: [0.9, 1.02, 1.05],
              transition: { duration: 0.34, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.72, 1] },
            }),
          ]);

          if (!mounted.current) return;
          playTradeSfx(sfx.beat || "cardShuffle");

          // fake throw outward, then snap back
          await Promise.all([
            cardA.start({
              x: [0, 120, 18],
              y: [0, -60, -6],
              rotateZ: [0, 20, 5],
              transition: { duration: 0.22, ease: "easeInOut", times: [0, 0.58, 1] },
            }),
            cardB.start({
              x: [0, -120, -18],
              y: [0, 60, 6],
              rotateZ: [0, -20, -5],
              transition: { duration: 0.22, ease: "easeInOut", times: [0, 0.58, 1] },
            }),
          ]);

          setZA(1);
          setZB(2);
          flash.start({
            opacity: [0, 0.9, 0],
            transition: { duration: 0.14, ease: "linear" },
          });
          playTradeSfx(sfx.swap);

          await Promise.all([
            cardA.start({
              x: [18, -130, -260],
              y: [-6, 18, 110],
              rotateZ: [5, -8, -18],
              scale: [1.05, 1, 0.92],
              opacity: [1, 1, 0],
              transition: { duration: 0.42, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.55, 1] },
            }),
            cardB.start({
              x: [-18, 130, 260],
              y: [6, -18, -110],
              rotateZ: [-5, 8, 18],
              scale: [1.05, 1.04, 1.1],
              opacity: [1, 1, 0],
              transition: { duration: 0.42, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.55, 1] },
            }),
          ]);
        },
      },

      7: {
        run: async () => {
          // vertical rise/drop to center
          await Promise.all([
            cardA.start({
              x: [0, 0],
              y: [300, 0],
              rotateZ: [-12, 0],
              rotateY: [18, 0],
              scale: [0.88, 1.03],
              transition: { duration: 0.4, ease: [0.22, 0.61, 0.36, 1] },
            }),
            cardB.start({
              x: [0, 0],
              y: [-300, 0],
              rotateZ: [12, 0],
              rotateY: [-18, 0],
              scale: [0.88, 1.03],
              transition: { duration: 0.4, ease: [0.22, 0.61, 0.36, 1] },
            }),
          ]);

          if (!mounted.current) return;
          playTradeSfx(sfx.beat || "cardShuffle");

          // helix-like cross by opposite arcs
          setZA(2);
          setZB(1);
          await Promise.all([
            cardA.start({
              x: [0, -80, 40, 0],
              y: [0, -44, -12, 0],
              rotateZ: [0, -20, 8, 0],
              rotateY: [0, -30, 14, 0],
              transition: { duration: 0.34, ease: "easeInOut", times: [0, 0.4, 0.75, 1] },
            }),
            cardB.start({
              x: [0, 80, -40, 0],
              y: [0, 44, 12, 0],
              rotateZ: [0, 20, -8, 0],
              rotateY: [0, 30, -14, 0],
              transition: { duration: 0.34, ease: "easeInOut", times: [0, 0.4, 0.75, 1] },
            }),
          ]);

          setZA(1);
          setZB(2);
          flash.start({
            opacity: [0, 0.82, 0],
            transition: { duration: 0.16, ease: "linear" },
          });
          playTradeSfx(sfx.swap);

          await Promise.all([
            cardA.start({
              x: [0, -30, -90],
              y: [0, -120, -260],
              rotateZ: [0, -4, 6],
              rotateY: [0, -12, 0],
              opacity: [1, 1, 0],
              transition: { duration: 0.46, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.54, 1] },
            }),
            cardB.start({
              x: [0, 30, 90],
              y: [0, 120, 260],
              rotateZ: [0, 4, -6],
              rotateY: [0, 12, 0],
              opacity: [1, 1, 0],
              transition: { duration: 0.46, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.54, 1] },
            }),
          ]);
        },
      },

      8: {
        run: async () => {
          // mirror-snap: appear near center quickly
          await Promise.all([
            cardA.start({
              x: [-280, -40],
              y: [0, 0],
              rotateZ: [-16, -4],
              scale: [0.92, 1.03],
              opacity: [1, 1],
              transition: { duration: 0.26, ease: [0.22, 0.61, 0.36, 1] },
            }),
            cardB.start({
              x: [280, 40],
              y: [0, 0],
              rotateZ: [16, 4],
              scale: [0.92, 1.03],
              opacity: [1, 1],
              transition: { duration: 0.26, ease: [0.22, 0.61, 0.36, 1] },
            }),
          ]);

          if (!mounted.current) return;
          playTradeSfx(sfx.beat || "cardShuffle");

          // quick in-place flicks
          await Promise.all([
            cardA.start({
              x: [-40, -8, -20],
              y: [0, -16, 0],
              rotateZ: [-4, 14, 3],
              transition: { duration: 0.16, ease: "easeInOut", times: [0, 0.5, 1] },
            }),
            cardB.start({
              x: [40, 8, 20],
              y: [0, 16, 0],
              rotateZ: [4, -14, -3],
              transition: { duration: 0.16, ease: "easeInOut", times: [0, 0.5, 1] },
            }),
          ]);

          setZA(1);
          setZB(2);
          flash.start({
            opacity: [0, 0.92, 0],
            transition: { duration: 0.11, ease: "linear" },
          });
          playTradeSfx(sfx.swap);

          // mirror teleport feeling: immediate side swap then fade
          await Promise.all([
            cardA.start({
              x: [-20, 120, 180],
              y: [0, 0, -20],
              rotateZ: [3, -10, -16],
              opacity: [1, 1, 0],
              transition: { duration: 0.28, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.58, 1] },
            }),
            cardB.start({
              x: [20, -120, -180],
              y: [0, 0, 20],
              rotateZ: [-3, 10, 16],
              opacity: [1, 1, 0],
              transition: { duration: 0.28, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.58, 1] },
            }),
          ]);
        },
      },
    };

    const raf = requestAnimationFrame(() => {
      (async () => {
        if (!mounted.current) return;
        setZA(2);
        setZB(1);
        await flash.set({ opacity: 0 });
        await Promise.all([
          cardA.set(initialFor("A")),
          cardB.set(initialFor("B")),
        ]);
        if (!mounted.current) return;
        const chosen = VARIANTS[version] || VARIANTS[1];
        await chosen.run();
        if (!mounted.current) return;
        onFinish?.();
      })();
    });

    return () => {
      mounted.current = false;
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, timestamp]);

  const initialFor = (which) => {
    if (version === 2) {
      return which === "A"
        ? { x: 0, y: 300, opacity: 1, rotateX: 13, rotateY: 0, transformPerspective: 800 }
        : { x: 0, y: -300, opacity: 1, rotateX: 45, rotateY: 0, transformPerspective: 800 };
    }
    if (version === 3) {
      return which === "A"
        ? { x: -360, y: 120, opacity: 1, rotateZ: -10, rotateX: 8, transformPerspective: 700 }
        : { x: 360, y: -120, opacity: 1, rotateZ: 10, rotateX: 8, transformPerspective: 700 };
    }
    if (version === 4) {
      return which === "A"
        ? { x: 0, y: 260, opacity: 1, rotateX: 18, rotateZ: -4, transformPerspective: 780 }
        : { x: 0, y: -260, opacity: 1, rotateX: 18, rotateZ: 4, transformPerspective: 780 };
    }
    if (version === 5) {
      return which === "A"
        ? { x: -260, y: 0, opacity: 1, rotateZ: -12, rotateX: 10, transformPerspective: 720 }
        : { x: 260, y: 0, opacity: 1, rotateZ: 12, rotateX: 10, transformPerspective: 720 };
    }
    if (version === 6) {
      return which === "A"
        ? { x: -200, y: 220, opacity: 1, rotateZ: -18, rotateX: 10, transformPerspective: 740 }
        : { x: 200, y: -220, opacity: 1, rotateZ: 18, rotateX: 10, transformPerspective: 740 };
    }
    if (version === 7) {
      return which === "A"
        ? { x: 0, y: 300, opacity: 1, rotateZ: -12, rotateY: 18, transformPerspective: 760 }
        : { x: 0, y: -300, opacity: 1, rotateZ: 12, rotateY: -18, transformPerspective: 760 };
    }
    if (version === 8) {
      return which === "A"
        ? { x: -280, y: 0, opacity: 1, rotateZ: -16, rotateX: 8, transformPerspective: 740 }
        : { x: 280, y: 0, opacity: 1, rotateZ: 16, rotateX: 8, transformPerspective: 740 };
    }
    return which === "A"
      ? { x: -200, y: 400, opacity: 1, skewX: -12 }
      : { x: 200, y: -400, opacity: 1, skewX: -12 };
  };

  const commonImgStyle =
    version === 1
      ? {
          width: size,
          height: size * 1.4,
          transform: "skewX(-12deg)",
          boxShadow: "0px 4px 5px rgba(0,0,0,0.4)",
          borderRadius: 8,
        }
      : {
          width: size,
          height: size * 1.4,
          boxShadow: "0px 10px 18px rgba(0,0,0,0.45)",
          transformStyle: "preserve-3d",
          transformOrigin: "center center",
          borderRadius: 8,
        };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex,
        pointerEvents: "none",
      }}
    >
      <motion.div
        animate={flash}
        initial={{ opacity: 0 }}
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: flashColor,
          zIndex: zIndex + 7000,
        }}
      />

      <motion.img
        src={imgSrc}
        alt="Trade Card A"
        animate={cardA}
        initial={initialFor("A")}
        style={{
          position: "absolute",
          zIndex: zA,
          ...commonImgStyle,
        }}
      />

      <motion.img
        src={imgSrc}
        alt="Trade Card B"
        animate={cardB}
        initial={initialFor("B")}
        style={{
          position: "absolute",
          zIndex: zB,
          ...commonImgStyle,
        }}
      />
    </div>
  );
}

function wait(sec) {
  return new Promise((res) => setTimeout(res, sec * 1000));
}
